import { useEffect, useState } from "react";
import BackofficeLayout, { Card, Btn, fmtEUR } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Wand2, RotateCcw, TrendingUp, TrendingDown } from "lucide-react";

const emptyScenario = { project_id: "", entity_id: "",
                         budget_overrun_pct: 0, delay_months: 0,
                         interest_rate_delta: 0, vacancy_months: 0 };

export default function Simulations() {
  const [projects, setProjects] = useState([]);
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState(emptyScenario);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/backoffice/projects").then((r) => r.data),
      api.get("/backoffice/entities").then((r) => r.data),
    ]).then(([p, e]) => { setProjects(p); setEntities(e); });
  }, []);

  const runSim = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        project_id: form.project_id || null,
        entity_id: form.entity_id || null,
        budget_overrun_pct: parseFloat(form.budget_overrun_pct) || 0,
        delay_months: parseInt(form.delay_months) || 0,
        interest_rate_delta: parseFloat(form.interest_rate_delta) || 0,
        vacancy_months: parseInt(form.vacancy_months) || 0,
      };
      const r = await api.post("/backoffice/simulate", body);
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur simulation");
    } finally { setBusy(false); }
  };

  const preset = (p) => setForm({ ...form, ...p });

  return (
    <BackofficeLayout
      title="Simulations financières"
      subtitle="Testez l'impact d'un dépassement de budget, d'un retard chantier, d'une hausse de taux ou d'une vacance locative."
    >
      <Toaster theme="dark" position="top-right" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card data-testid="bof-sim-form-card">
          <div className="font-bold text-white text-sm mb-4 flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-amber-400" /> Paramètres du scénario
          </div>
          <form onSubmit={runSim} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <F label="Périmètre — Projet">
                <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value, entity_id: "" })} className="input" data-testid="bof-sim-project">
                  <option value="">Tous les projets</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </F>
              <F label="ou par Entité">
                <select value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value, project_id: "" })} className="input" data-testid="bof-sim-entity">
                  <option value="">Toutes entités</option>
                  {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </F>
            </div>

            <Slider label="Dépassement budget"
                    value={form.budget_overrun_pct} onChange={(v) => setForm({ ...form, budget_overrun_pct: v })}
                    min={0} max={50} step={5} unit="%" testid="bof-sim-budget" />

            <Slider label="Retard chantier"
                    value={form.delay_months} onChange={(v) => setForm({ ...form, delay_months: v })}
                    min={0} max={12} step={1} unit=" mois" testid="bof-sim-delay" />

            <Slider label="Hausse taux d'intérêt"
                    value={form.interest_rate_delta} onChange={(v) => setForm({ ...form, interest_rate_delta: v })}
                    min={0} max={5} step={0.25} unit=" pts" testid="bof-sim-rate" />

            <Slider label="Vacance locative"
                    value={form.vacancy_months} onChange={(v) => setForm({ ...form, vacancy_months: v })}
                    min={0} max={12} step={1} unit=" mois" testid="bof-sim-vacancy" />

            <div className="flex flex-wrap gap-2 pt-2">
              <button type="button" onClick={() => preset({ budget_overrun_pct: 10, delay_months: 3, interest_rate_delta: 0.5, vacancy_months: 1 })}
                      className="text-xs px-2.5 py-1.5 border border-slate-700 hover:bg-slate-800 rounded text-slate-300">
                Scénario prudent
              </button>
              <button type="button" onClick={() => preset({ budget_overrun_pct: 20, delay_months: 6, interest_rate_delta: 1.5, vacancy_months: 3 })}
                      className="text-xs px-2.5 py-1.5 border border-amber-700 bg-amber-500/10 hover:bg-amber-500/20 rounded text-amber-300">
                Scénario stressé
              </button>
              <button type="button" onClick={() => setForm(emptyScenario)}
                      className="text-xs px-2.5 py-1.5 border border-slate-700 hover:bg-slate-800 rounded text-slate-400 inline-flex items-center gap-1.5">
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            </div>

            <Btn type="submit" disabled={busy} className="w-full justify-center" data-testid="bof-sim-run">
              {busy ? "Calcul…" : "Lancer la simulation"}
            </Btn>
          </form>
        </Card>

        {/* Result */}
        <Card data-testid="bof-sim-result-card">
          <div className="font-bold text-white text-sm mb-4">Résultat</div>
          {!result ? (
            <div className="text-slate-500 text-sm py-12 text-center">Ajustez les paramètres et lancez la simulation.</div>
          ) : (
            <div className="space-y-4">
              <div className={`p-3 rounded border text-sm ${result.scenario.cash_flow_mensuel_new < 0 ? "bg-red-500/10 border-red-800 text-red-200" : "bg-emerald-500/10 border-emerald-800 text-emerald-200"}`}
                   data-testid="bof-sim-reco">
                {result.recommandation}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <MetricCompare label="Cash-flow / mois"
                               base={result.baseline.cash_flow_mensuel}
                               after={result.scenario.cash_flow_mensuel_new} />
                <MetricCompare label="Mensualité prêts"
                               base={result.baseline.mensualite_prets}
                               after={result.scenario.mensualite_prets_new}
                               invert />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Impacts financiers</div>
                <div className="space-y-2 text-sm">
                  <Row label="Dépassement budget" value={fmtEUR(result.scenario.budget_impact)} negative={result.scenario.budget_impact > 0} />
                  <Row label="Perte vacance locative" value={fmtEUR(result.scenario.perte_vacance)} negative={result.scenario.perte_vacance > 0} />
                  <Row label="Surcoût retard chantier" value={fmtEUR(result.scenario.surcout_retard)} negative={result.scenario.surcout_retard > 0} />
                  <div className="h-px bg-slate-800 my-2" />
                  <Row label="Impact trésorerie sur 12 mois"
                       value={fmtEUR(result.scenario.impact_tresorerie_12m)}
                       negative={result.scenario.impact_tresorerie_12m < 0}
                       bold />
                </div>
              </div>

              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer hover:text-slate-300">Paramètres baseline</summary>
                <pre className="mt-2 p-2 bg-slate-900/60 border border-slate-800 rounded overflow-x-auto">{JSON.stringify(result.baseline, null, 2)}</pre>
              </details>
            </div>
          )}
        </Card>
      </div>

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">{label}</span>
    {children}
  </label>
);

const Slider = ({ label, value, onChange, min, max, step, unit, testid }) => (
  <div>
    <div className="flex justify-between items-baseline mb-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <span className="text-sm font-bold text-amber-300 font-mono">+{value}{unit}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
           onChange={(e) => onChange(parseFloat(e.target.value))}
           className="w-full accent-amber-400" data-testid={testid} />
  </div>
);

const MetricCompare = ({ label, base, after, invert = false }) => {
  const diff = after - base;
  const good = invert ? diff <= 0 : diff >= 0;
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-lg font-black text-white">{fmtEUR(after)}</span>
        <span className={`text-xs font-mono ${good ? "text-emerald-300" : "text-red-300"}`}>
          {good ? <TrendingUp className="w-3 h-3 inline" /> : <TrendingDown className="w-3 h-3 inline" />} {diff >= 0 ? "+" : ""}{fmtEUR(diff)}
        </span>
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">Base: {fmtEUR(base)}</div>
    </div>
  );
};

const Row = ({ label, value, negative, bold }) => (
  <div className="flex justify-between items-center">
    <span className={`${bold ? "font-bold text-white" : "text-slate-300"}`}>{label}</span>
    <span className={`font-mono ${bold ? "text-base font-black" : ""} ${negative ? "text-red-300" : "text-emerald-300"}`}>{value}</span>
  </div>
);
